import { LitElement, TemplateResult, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { shared } from "../styles";
import {
    filterDateTimeByRangeInclusive,
    filterDateTimeBeforeToday,
    type TemporalEvent,
    filterDateTimeFromToday,
} from "../utils/temporal";

type GroupedEvents<T extends TemporalEvent> = {
    upcoming: Record<string, Array<T>>;
    past: Record<string, Array<T>>;
    fallback: Record<string, Array<T>>;
};

@customElement("ptc-event-list")
export class EventList<EventListItem extends TemporalEvent> extends LitElement {
    @property({ type: Array })
    timeWindow: [from: Temporal.PlainDateTime, to: Temporal.PlainDateTime];

    @property({ attribute: false })
    dateFormat: Intl.DateTimeFormat;

    @property({ type: Array })
    events: Array<EventListItem> = [];

    @property({ type: String })
    mode: "compact" | "detailed" = "compact";

    @property({ type: Boolean })
    useFallback: boolean = true;

    @property()
    itemTemplate: (event: EventListItem, index: number) => TemplateResult = (event, index) =>
        html`<li part="item">${index} - ${event.date.toLocaleString()}</li>`;

    static styles = [
        shared,
        css`
            :host {
                display: block;
            }

            [part="datelist"] {
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }

            [part="dateitem"] {
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }

            [part="date"] {
                font-size: 1rem;
                font-weight: 600;
            }

            [part="eventlist"] {
                display: flex;
                flex-direction: column;
                gap: 1rem;
            }

            [part="eventitem"] {
                display: flex;
                align-items: center;
                gap: 1rem;
            }

            [data-mode="compact"] {
                [part="date"] {
                    font-size: 1rem;
                }

                [part="dateitem"] {
                    display: grid;
                    grid-template-columns: min-content auto;
                }

                ptc-avatar {
                    display: none;
                }
            }

            [data-event-mode="past"] {
                opacity: 0.5;
            }
        `,
    ];

    @state()
    private _groupedEvents: GroupedEvents<EventListItem> = {
        upcoming: {},
        past: {},
        fallback: {},
    };

    willUpdate() {
        const { pastEvents, upcomingEvents, fallbackEvents } = categorizeEvents(this.events, this.timeWindow);

        this._groupedEvents = {
            upcoming: Object.groupBy(upcomingEvents, (event) => {
                return event.date.toPlainDate().toString();
            }) as Record<string, Array<EventListItem>>,

            past: Object.groupBy(pastEvents, (event) => {
                return event.date.toPlainDate().toString();
            }) as Record<string, Array<EventListItem>>,

            fallback: Object.groupBy(fallbackEvents, (event) => {
                return event.date.toPlainDate().toString();
            }) as Record<string, Array<EventListItem>>,
        };

        // have at least 3 events or use fallback
        this.useFallback =
            Object.keys([...Object.keys(this._groupedEvents.upcoming), ...Object.keys(this._groupedEvents.past)])
                .length < 3;
    }

    renderEventList(events: Array<EventListItem>, date: string, mode: "past" | "upcoming") {
        return html`
            <div part="dateitem" role="listitem" data-event-mode=${mode}>
                <time datetime=${date} part="date">${this.dateFormat.format(new Date(date))}</time>
                <ul part="eventlist" role="list">
                    ${events.map((event, index) => html`<li part="eventitem">${this.itemTemplate(event, index)}</li>`)}
                </ul>
            </div>
        `;
    }

    render() {
        if (this.events.length === 0) {
            return html`
                <div part="empty-message">
                    <slot name="empty-message">Keine Events in den nächsten Tagen</slot>
                </div>
            `;
        }

        return html`
            <div part="datelist" role="list" data-mode=${this.mode}>
                ${this.useFallback
                    ? Object.entries(this._groupedEvents.fallback)
                          .slice(0, 3)
                          .map(([date, events]) => html` ${this.renderEventList(events, date, "upcoming")} `)
                    : html`
                          ${Object.entries(this._groupedEvents.past).map(
                              ([date, events]) => html` ${this.renderEventList(events, date, "past")} `
                          )}
                          ${Object.entries(this._groupedEvents.upcoming).map(
                              ([date, events]) => html` ${this.renderEventList(events, date, "upcoming")} `
                          )}
                      `}
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-event-list": EventList<TemporalEvent>;
    }
}

function categorizeEvents(
    events: Array<TemporalEvent>,
    timeWindow: [from: Temporal.PlainDateTime, to: Temporal.PlainDateTime]
) {
    const filteredEvents = events
        .filter((e) => filterDateTimeByRangeInclusive(e.date, timeWindow))
        .sort((a, b) => Temporal.PlainDateTime.compare(a.date, b.date));

    const pastEvents = filteredEvents.filter((e) => filterDateTimeBeforeToday(e.date));
    const upcomingEvents = filteredEvents.filter((e) => filterDateTimeFromToday(e.date));

    // using all events for the fallback
    const fallbackEvents = events
        .filter((e) => filterDateTimeFromToday(e.date))
        .sort((a, b) => Temporal.PlainDateTime.compare(a.date, b.date));

    return { pastEvents, upcomingEvents, fallbackEvents };
}
